/**
 * Signing in, signing out, and knowing which of those is currently true.
 *
 * Talks to `/auth` and nothing else, the same way `jmap.ts` talks only to
 * `/jmap` and `accounts.ts` only to `/accounts`. The separation is not
 * tidiness: it keeps each module's answer to "what may this call do" readable
 * in one file. There is no module for `/admin` any more -- that surface
 * administers every tenant on the box and now lives in a separate panel on a
 * separate origin, which is the only boundary a browser actually enforces.
 *
 * The credential itself never appears here. A successful login sets an
 * HttpOnly cookie, which script cannot read by design — so there is nothing to
 * store, nothing to attach to later requests, and nothing for injected script
 * to steal out of this origin. What this module holds is the *answer* to "am I
 * signed in", which is not a secret.
 */

// The transport seam. `@transport` resolves at build time to the browser
// implementation or the desktop one; nothing below can tell which. Imported
// under the name `fetch` so the call sites are untouched and this import is
// the whole of the difference. See `lib/net.types.ts`.
import {
  carriesSessionCookie,
  request as fetch,
  requestToken as transportToken,
} from "@transport";
import { signal } from "@preact/signals";

import { MIRROR_KEY, TABS_KEY } from "./keys";
import type { TokenRefusal } from "./net.types";
import { clearPublicSession, markPublicSession } from "./publicSession";
import { deviceToken, authHeaders, deviceTokenWritten } from "./settings";
import * as tabs from "./tabs";

/**
 * Whether the client believes it can reach mail.
 *
 * `unknown` is the startup state and matters: a client that assumes "signed
 * out" would flash a login form at someone who is signed in, and one that
 * assumes "signed in" would render an empty mailbox at someone who is not.
 *
 * `unreachable` is separated from `out` because the two need opposite
 * responses. A 401 means show the login form; a dead daemon means say the
 * daemon is unreachable, because presenting a password box to someone whose
 * password cannot possibly be checked invites them to conclude they typed it
 * wrong.
 */
export type AuthState = "unknown" | "in" | "out" | "unreachable";

export const authState = signal<AuthState>("unknown");

/** Who is signed in, when anyone is. */
export const identity = signal<Identity | null>(null);

/** From `/ready`: what this deployment can actually offer a person. */
export const capability = signal<Capability | null>(null);

export interface Identity {
  /** Null for a session opened with a pasted device token rather than a password. */
  username: string | null;
  tenant: string;
  displayName: string;
}

export interface Capability {
  /** False on a fresh install: nobody has a password yet. */
  loginsConfigured: boolean;
  /** False when the daemon will refuse a password over this transport. */
  passwordLogin: boolean;
  /**
   * Whether a Google OAuth client is installed, and therefore whether "Connect
   * a Google account" can work at all. A button that can only ever fail is
   * worse than an absent one with a sentence saying what an operator must set.
   *
   * Kept beside {@link oauthProviders} rather than replaced by it, because the
   * daemon still sends `oauth_configured` for clients built before there were
   * two issuers. New code should read the list.
   */
  oauthConfigured: boolean;

  /**
   * The provider kinds this deployment can run an OAuth flow for, in the order
   * a settings screen should offer them: `["gmail", "microsoft"]`.
   *
   * A list rather than a second boolean because "is OAuth configured" stopped
   * being a yes-or-no question the moment there were two issuers: a deployment
   * with a Google project and no Entra registration has to draw one button and
   * not the other, and one flag cannot say that.
   *
   * Empty against a daemon too old to send it, *except* that
   * {@link refreshCapability} fills in `gmail` from the older boolean — so a
   * new client against an old daemon still draws the Google button rather than
   * silently losing it.
   */
  oauthProviders: string[];
}

export interface SessionRow {
  id: number;
  label: string;
  origin: "cli" | "login";
  createdAt: number;
  lastSeenAt: number | null;
  expiresAt: number | null;
  current: boolean;
}

export class AuthError extends Error {
  readonly status: number;
  /**
   * The daemon's machine-readable name for what went wrong, where it sent one.
   *
   * `problem` and `error` are siblings in the same body: the second is a
   * sentence for a person and the first is a word for this client. **Branch on
   * this and never on the message.** The sentence for "you have not given me a
   * code" and the one for "that code was not accepted" are deliberately
   * different so that somebody reading it learns something, and they are the
   * same `problem` — a client that matched the words would treat a mistyped
   * digit as a fresh sign-in and make it cost the password as well.
   *
   * Undefined against a daemon that predates the field, and against every error
   * that is not the daemon's: a 502 from nginx is an HTML page and a dropped
   * connection is no answer at all. So absence means "nothing was named", which
   * is exactly the case where the sentence is all there is.
   */
  readonly problem: string | undefined;
  constructor(message: string, status: number, problem?: string) {
    super(message);
    this.name = "AuthError";
    this.status = status;
    this.problem = problem;
  }
}

/**
 * The password was right, and the credential could not be kept.
 *
 * Not an {@link AuthError} and deliberately not carrying a `problem`: nothing
 * about this came from the daemon. The daemon said yes, minted a session and
 * handed over a token; what refused was the platform store on this machine, and
 * a screen that filed it under "the server rejected you" would send somebody to
 * check a password that was never the problem.
 *
 * Only reachable on the bearer door -- the browser's credential is a cookie it
 * never sees and cannot fail to keep.
 */
export class CredentialStoreError extends Error {
  /**
   * The store's own words, passed through untouched.
   *
   * This is the only clue that exists on a real machine: it is the difference
   * between "your keychain is locked" and "there is no Secret Service running
   * in this session", and neither status code nor exception type distinguishes
   * them. It is carried separately from {@link Error.message} so the screen can
   * put it inside a sentence of its own rather than showing a message written
   * for a log.
   */
  readonly reason: string;
  constructor(reason: string) {
    super(`the credential could not be stored: ${reason}`);
    this.name = "CredentialStoreError";
    this.reason = reason;
  }
}

/**
 * The `problem` names this client acts on, spelled once.
 *
 * The daemon owns these strings; they are copied here rather than derived
 * because there is nowhere to derive them from, and a literal typed at three
 * call sites is a literal that will be mistyped at one of them. A name this
 * client does not know falls through to showing the sentence, which is the
 * right answer for a `problem` invented after this build shipped.
 */
/** No valid code was given for an account that has one. Ask for a code. */
export const TOTP_REQUIRED = "totp_required";
/** Too many codes tried. Say so, and keep the field: this passes. */
export const TOTP_THROTTLED = "totp_throttled";
/** The code was wrong, on a request that was otherwise fine. */
export const TOTP_BAD_CODE = "totp_bad_code";
/** Nothing to confirm: the pending secret expired, or was never made. */
export const TOTP_NO_PENDING = "totp_no_pending";
/** This deployment has no master key, so a secret cannot be stored at all. */
export const TOTP_UNAVAILABLE = "totp_unavailable";

/**
 * The daemon's name for a failure, out of whatever was thrown.
 *
 * A function rather than `e instanceof AuthError && e.problem` written at each
 * of the six places that ask, because the screens are the ones asking and a
 * screen that gets the `instanceof` wrong fails open: the check is false, the
 * sentence is shown, and the code field never appears. That is a login nobody
 * can complete, produced by a missing branch rather than by a thrown error, so
 * it would reach somebody before it reached a test.
 */
export function problemOf(e: unknown): string | undefined {
  return e instanceof AuthError ? e.problem : undefined;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(path, {
    method,
    // Same-origin is already the default, but the cookie *is* the credential
    // here and a default worth relying on is a default worth stating.
    credentials: "same-origin",
    // Always JSON, including on bodyless writes: the daemon refuses a
    // cookie-authenticated write without it, which is what stops a cross-site
    // form from forging one.
    // `authHeaders` rather than a bare content-type: it sends the same
    // content-type and adds the bearer when this build has one. A browser has
    // none and is authenticated by its cookie; a packaged build has no cookie,
    // and this line is the whole of what makes the request authenticated at
    // all. See `lib/settings.ts` for what leaving it out cost.
    headers: authHeaders(),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (!res.ok) {
    let detail = res.statusText;
    let problem: string | undefined;
    try {
      const document = await res.json();
      detail = document.error ?? document.detail ?? detail;
      // The sibling key, read off the same body and kept beside the sentence
      // rather than instead of it. Guarded on the type because this is a
      // stranger's JSON: a `problem` that arrived as a number would otherwise
      // be compared against string constants forever and never match, which is
      // the quietest possible way to lose a branch.
      if (typeof document.problem === "string") problem = document.problem;
    } catch {
      // A proxy error page is HTML; the status line is the message.
    }
    throw new AuthError(detail, res.status, problem);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Whatever the bearer door rejected with, as the error the screens read.
 *
 * The transport is the only thing that has seen the response, so it is the only
 * thing that could have carried the status and the `problem` back across the
 * seam -- see {@link TokenRefusal}. This puts them in the shape the rest of the
 * client already understands, and invents nothing when they are absent: 0 is
 * not a status anybody can branch on, which is the honest answer for a
 * connection that never got one.
 */
function asAuthError(e: unknown): AuthError {
  if (e instanceof AuthError) return e;
  const refusal = e as TokenRefusal;
  const message = e instanceof Error ? e.message : String(e);
  return new AuthError(message, refusal?.status ?? 0, refusal?.problem);
}

/**
 * Ask the server who we are.
 *
 * This is the gate the whole app hangs off, and it replaces a check that was
 * simply wrong: the client used to decide it was signed in whenever a token
 * string was non-empty, so a revoked or expired credential rendered as a
 * working mail UI with an error banner and no way back to a login screen.
 */
export async function refreshIdentity(): Promise<void> {
  try {
    const who = await request<Identity>("GET", "/auth/whoami");
    identity.value = who;
    authState.value = "in";
  } catch (e) {
    if (e instanceof AuthError && e.status === 401) {
      identity.value = null;
      authState.value = "out";
      return;
    }
    // Anything else -- a 502 from nginx, a dropped connection, a daemon
    // mid-restart -- is not evidence about the credential.
    authState.value = "unreachable";
  }
}

/**
 * What this deployment can offer, read from the unauthenticated probe.
 *
 * Lets the login screen say "no password has been set, run this command"
 * instead of presenting a form that cannot succeed, and warn about plain HTTP
 * before someone types into it rather than after. The accounts screen reads it
 * for the same reason: whether this deployment can offer Gmail at all.
 *
 * Unauthenticated, so it is safe to ask before signing in and worth asking
 * again after -- someone who arrived with a live cookie never saw the login
 * screen, and never triggered the only other call.
 */
export async function refreshCapability(): Promise<void> {
  try {
    const res = await fetch("/ready", { credentials: "same-origin" });
    if (!res.ok) return;
    const ready = await res.json();
    capability.value = {
      loginsConfigured: ready.logins_configured === true,
      passwordLogin: ready.password_login === true,
      oauthConfigured: ready.oauth_configured === true,
      // The forward-compatible read, with the old field as the fallback: a
      // daemon that predates `oauth_providers` still answers the only question
      // it could, and losing the Google button against it would be a
      // regression dressed up as a new feature.
      oauthProviders: Array.isArray(ready.oauth_providers)
        ? ready.oauth_providers.filter((p: unknown) => typeof p === "string")
        : ready.oauth_configured === true
          ? ["gmail"]
          : [],
    };
  } catch {
    // Advisory only. A login form with no capability hints still works.
  }
}

/**
 * Sign in.
 *
 * `ephemeral` is the "this is a public or shared computer" box on the login
 * screen, and it asks for two different things at once. The daemon's half is in
 * the request: a twelve-hour session, a cookie that dies with the browser, and
 * a label saying so in the session list. The client's half is
 * {@link markPublicSession}, after which nothing in this app writes to the
 * machine -- see `lib/publicSession.ts` for the whole of that rule.
 *
 * `totp` is a code from an authenticator app, and it is absent on the first
 * attempt every time -- deliberately, because there is no way to know from here
 * whether this account has a second factor or whether this address is one the
 * daemon exempts. The screen learns that from the refusal: a 401 whose
 * `problem` is {@link TOTP_REQUIRED} means "ask for a code and come back", and
 * `ui/Login.tsx` does exactly that with the password it still holds. Sending an
 * empty string instead of nothing would be a client claiming to have tried a
 * code, and is what the `?? undefined` at each call site prevents.
 */
export async function login(
  username: string,
  password: string,
  ephemeral = false,
  totp?: string,
): Promise<void> {
  // Two doors, and which one this build uses is decided by whether a cookie
  // would ever come back.
  //
  // `/auth/login` answers with an HttpOnly cookie, which is the right thing for
  // a browser precisely because script cannot read it. A build whose requests
  // are issued outside the page never gets that cookie sent back, so it asks
  // `/auth/token` for the credential itself and keeps it where the platform
  // keeps secrets. The daemon refuses to issue one to anything carrying browser
  // fetch metadata, so this is a choice about which door works rather than
  // which is safer to walk through.
  //
  // `ephemeral` is not offered on this door and is ignored if it arrives
  // anyway. It is a statement about a cookie and about this origin's
  // `localStorage`, and this build has neither: the checkbox that sets it is
  // drawn only where `daemonAddress` is null. Honouring it here would gate the
  // token write below -- which on this build *is* the credential -- and produce
  // the project's most-repeated bug for the fourth time.
  if (!carriesSessionCookie) {
    // Through the transport rather than as an ordinary request, because *how*
    // it is issued decides whether it is allowed at all: the daemon refuses to
    // hand a bearer token to a browser, and a webview's HTTP client stamps its
    // own `Origin` on anything sent from the page. See `lib/net.types.ts`.
    //
    // Wrapped, because the transport parses the daemon's answer on the far side
    // of the seam and rejects with a plain `Error` shaped object rather than an
    // `AuthError`. Everything above this module reads the failure as an
    // `AuthError` -- the sign-in screen asks {@link problemOf} whether to draw a
    // code field -- so the two doors have to fail alike, or the second factor
    // would work in a browser and be a dead end on the desktop and on Android.
    let token: string;
    try {
      token = await transportToken(username, password, describeBrowser(), totp);
    } catch (e) {
      throw asAuthError(e);
    }

    // Before `authState` moves, so nothing can make a request in the window
    // where this client believes it is signed in and has no credential to
    // prove it.
    deviceToken.value = token;

    /*
     * And the store has to have *taken* it before `authState` moves either.
     *
     * # Why this waits at all
     *
     * Setting the signal starts the write and nothing used to look at how it
     * went, so a keychain that refused rejected a promise nobody held. On a Mac
     * launched outside its Aqua session that is `errSecInteractionNotAllowed`,
     * and the whole of what a person saw was the login screen coming back and
     * staying there -- the reason existed, in one line of the shell log, which
     * is nowhere.
     *
     * # Why a refusal stays on the login screen instead of entering the app
     *
     * This is the choice, and it is made here rather than left to the screen.
     * A signed-in session whose token is only in this page's memory would be a
     * mailbox that renders and loads nothing: on this build the *Rust* side
     * reads the credential straight out of the platform store for every request
     * that matters -- `jmap_request`, the event stream, replication, the
     * updater, all `device_token_load(...).unwrap_or_default()` in
     * `src-tauri/src/net.rs` and `stream.rs` -- and does not consult the page at
     * all. So an unstored token authenticates `/auth/whoami`, which goes out
     * with headers from here, and nothing else. The person would be shown a
     * sidebar, an empty message list, and 401s.
     *
     * That is this project's most-repeated bug wearing a new hat: sign in
     * successfully, be refused by everything afterwards, with nothing on screen
     * saying why. Entering the app would be choosing it on purpose. So the
     * sign-in does not complete, and the screen the person is already looking at
     * is told exactly what refused -- see `sentenceFor` in `ui/Login.tsx`.
     *
     * The token is left in the signal rather than rolled back. Clearing it would
     * mean asking the store to *delete* -- and a delete that succeeded where the
     * write failed would take away a working token this device was already
     * holding, turning a failed sign-in into a lost session. What is left behind
     * is a credential in memory that dies with the process, on a client that is
     * showing a login form: it costs nothing, and the next attempt overwrites it.
     */
    const unstored = await deviceTokenWritten();
    if (unstored !== null) throw new CredentialStoreError(unstored);

    authState.value = "in";
    // Identity comes from the server rather than from the login response: it is
    // one round trip, it is the same call the app makes on every launch, and it
    // means there is one answer to "who am I" instead of two that can differ.
    await refreshIdentity();
    return;
  }

  // Marked before the request rather than after it succeeds. Everything from
  // here to the mailbox -- the response, `refreshIdentity`, whatever the app
  // does on the way -- has to already be running under the rule, because the
  // first thing a signed-in client does is write things down.
  if (ephemeral) markPublicSession();

  let who: { username: string; tenant: string };
  try {
    who = await request<{ username: string; tenant: string }>(
      "POST",
      "/auth/login",
      {
        username,
        password,
        label: describeBrowser(),
        // Sent only when asked for, rather than always as a boolean. The daemon
        // defaults it to false, and an absent field is the one shape that cannot
        // be misread by a build of the daemon that predates it.
        ...(ephemeral ? { ephemeral: true } : {}),
        // The same rule, for the same reason, one field down. A login with no
        // code must be byte-for-byte the login this client has always sent --
        // `totp: null` or `totp: ""` is a new shape, and the daemon it would
        // meet on somebody's server is the one that has not been updated yet.
        ...(totp ? { totp } : {}),
      },
    );
  } catch (e) {
    // A login that did not happen leaves no session to protect, and a flag left
    // set would keep this tab in the mode for as long as it stays open --
    // silently, since nothing on the screen would say so after the form has
    // been dismissed.
    if (ephemeral) clearPublicSession();
    throw e;
  }
  // The session supersedes any device token this browser was holding, and the
  // token has to go rather than merely be ignored.
  //
  // This locked somebody out of a working account. `credential()` on the daemon
  // prefers a bearer header over the cookie -- correct, since a caller that
  // sends a token means it -- and `jmap.call` attaches one whenever `hm.token`
  // is a non-empty string. A browser left holding a revoked token from the era
  // of pasting them into Settings therefore logged in successfully, got a good
  // cookie, and then had every JMAP call answered 401, because the dead bearer
  // outranked the live cookie on every single request.
  //
  // It presented as "I cannot log in", which is the worst part: the login
  // itself worked every time, so nothing on the screen pointed here. Only the
  // pairing of a 200 on `/auth/whoami` -- cookie only, no bearer -- with a 401
  // on `/jmap/api` a second later says what is actually wrong.
  //
  // Only reached on the cookie path -- the token path returned above, having
  // just *stored* a token, and clearing it here would throw away the credential
  // it had gone to the trouble of obtaining. That is not hypothetical: an
  // unconditional clear here is what signed the desktop build out of the
  // session it had successfully created.
  deviceToken.value = "";
  identity.value = {
    username: who.username,
    tenant: who.tenant,
    displayName: who.tenant,
  };
  authState.value = "in";
  // The display name comes from whoami rather than the login response, so the
  // sidebar does not briefly show a slug where a name belongs.
  void refreshIdentity();
}

export async function logout(): Promise<void> {
  try {
    await request<void>("POST", "/auth/logout");
  } finally {
    // Even if the request failed. The cookie may already be gone, and leaving
    // someone looking at a mailbox they have been signed out of is worse than
    // one redundant login.
    identity.value = null;
    authState.value = "out";
    forgetThisMachine();
  }
}

/**
 * Everything this session left on the disk, at the moment it ends.
 *
 * **Not gated on the public-computer box, and that is the point.** Signing out
 * is somebody saying they are finished with this machine, and it has to mean
 * what they think it means -- which is not "the cookie is gone and the rest is
 * still in the browser's storage where the next person can open the devtools
 * and read it". The box changes what is written *during* a session; this is
 * what signing out is, for everyone.
 *
 * Three things go, and they are the three that are content rather than taste:
 *
 * - The composer's crash mirror. This is the one that decided the argument: it
 *   is the body of a message in plaintext, written on a keystroke timer, and it
 *   survives until something overwrites it. Nothing before this deleted it on
 *   sign-out.
 * - The device token. A credential, and one the daemon prefers over the cookie
 *   on every request -- so leaving it is not only a secret left behind, it is
 *   the thing that locks the *next* person out of their own account.
 * - The tab list. The addresses of somebody's mail, which is a smaller leak
 *   than the mirror and the same kind of one.
 *
 * Preferences stay, deliberately. A theme, a density, a column width and a
 * swipe binding say nothing about who was here, and wiping them would turn
 * signing out into a factory reset on the machine somebody signs out of every
 * evening -- which is how a feature like this gets switched off.
 */
function forgetThisMachine(): void {
  // The mode ends with the session that asked for it, so a later sign-in on
  // this tab is an ordinary one until somebody ticks the box again.
  clearPublicSession();

  // Through the signal rather than by deleting a key: the browser keeps this in
  // `localStorage` and the desktop build keeps it in the OS keychain, and only
  // the setter knows which. Reaching for `localStorage` here would clear the
  // browser's copy and quietly leave the keychain's.
  deviceToken.value = "";

  // The strip and the key. `reset()` puts the tabs back to the pinned mail view
  // -- otherwise the next person to sign in on this browser inherits an open
  // message they cannot load -- and its persist effect then writes that empty
  // state, which is correct and is still a key on the disk.
  tabs.reset();

  try {
    // The mirror is a *prefix*, not a key, and getting that wrong would leave
    // behind exactly the mail this sweep exists for. Each compose window keeps
    // its own crash guard under `hm.compose.mirror:<its address>` -- see
    // `mirrorKey` in `lib/compose.ts` -- because two composers sharing one key
    // overwrite each other. So sign-out has to take the family rather than the
    // name: a loop over the two literals cleared the main window's draft and
    // left every detached composer's on the disk.
    //
    // Read the names first. Removing while enumerating `localStorage` shifts
    // the indices under `key(i)`, which silently skips every other match.
    const doomed = [TABS_KEY];
    for (let i = 0; i < localStorage.length; i += 1) {
      const name = localStorage.key(i);
      if (name !== null && (name === MIRROR_KEY || name.startsWith(`${MIRROR_KEY}:`))) {
        doomed.push(name);
      }
    }
    for (const key of doomed) localStorage.removeItem(key);
  } catch {
    // Private-browsing modes throw on access. There is nothing to do about
    // it, and nothing was written there either.
  }
}

export function changePassword(current: string, next: string): Promise<void> {
  return request<void>("POST", "/auth/password", { current, next });
}

export function listSessions(): Promise<SessionRow[]> {
  return request<SessionRow[]>("GET", "/auth/sessions");
}

export function revokeSession(id: number): Promise<void> {
  return request<void>("DELETE", `/auth/sessions/${id}`);
}

/**
 * Where this account's second factor has got to.
 *
 * Two booleans and not one state, because they are answers to two different
 * questions and the interesting case is the one where they disagree.
 * `pending` without `enabled` is an enrolment somebody started and did not
 * finish -- the secret exists on the daemon, nothing is being enforced -- and a
 * screen that collapsed the pair into "off" would offer to start an enrolment
 * that silently replaces one, while a screen that collapsed it into "on" would
 * tell somebody they are protected when they are not.
 */
export interface TotpStatus {
  /** Enforced at sign-in. Only ever true of a confirmed secret. */
  enabled: boolean;
  /** A secret is waiting to be confirmed. Changes nothing on its own. */
  pending: boolean;
}

/** A secret the daemon has just minted, in the two spellings a person needs. */
export interface TotpEnrolment {
  /** Base32, for typing into an app by hand when a link cannot be followed. */
  secret: string;
  /** `otpauth://totp/...`, which is what a scanner or a click would consume. */
  uri: string;
}

export function totpStatus(): Promise<TotpStatus> {
  return request<TotpStatus>("GET", "/auth/totp");
}

/**
 * Mint a secret, and leave it pending.
 *
 * Nothing about sign-in changes until {@link totpConfirm} accepts a code from
 * it, which is the property that makes this safe to press: somebody who cannot
 * get their authenticator app to work has locked nobody out, and the pending
 * secret is simply replaced by the next attempt.
 *
 * `code` is a *current* code from the authenticator already on the account, and
 * it is required only when there is one -- the daemon answers 401
 * {@link TOTP_REQUIRED} without it. That is not ceremony: a session cookie is
 * something a borrowed laptop still has, and without this check the whole
 * feature could be moved to somebody else's phone by anyone who found a screen
 * left unlocked. Sent as a field only when present, following the rule
 * `ephemeral` and `totp` both follow on the login doors.
 */
export function totpEnroll(code?: string): Promise<TotpEnrolment> {
  return request<TotpEnrolment>("POST", "/auth/totp/enroll", code ? { code } : {});
}

/**
 * Prove the app and the daemon agree, and switch enforcement on.
 *
 * The code is the proof, and it is the only one available: a secret copied
 * wrongly, an app on a phone whose clock has drifted, or a QR that was never
 * scanned all produce a secret the daemon holds and the person cannot use.
 * Enforcing before this call would turn each of those into an account nobody
 * can sign in to, which is the failure this whole two-step shape exists to make
 * impossible.
 */
export function totpConfirm(code: string): Promise<void> {
  return request<void>("POST", "/auth/totp/confirm", { code });
}

/**
 * Turn it off, on production of a code that works.
 *
 * The code is asked for here for the reason it is asked for on enrolment, and
 * the answer is even plainer: a second factor that a live session can drop
 * without proving anything is a second factor that protects the account only
 * from people who cannot reach a settings screen.
 */
export function totpDisable(code: string): Promise<void> {
  return request<void>("POST", "/auth/totp/disable", { code });
}

/**
 * A label for the session list, so a person can tell their laptop from their
 * phone six weeks later.
 *
 * Coarse on purpose. This is stored on the server and shown in a list; a full
 * user-agent string would be a fingerprint kept somewhere it does not need to
 * be, to answer a question two words already answer.
 */
function describeBrowser(): string {
  const ua = navigator.userAgent;
  const engine = /Firefox\//.test(ua)
    ? "Firefox"
    : /Edg\//.test(ua)
      ? "Edge"
      : /Chrome\//.test(ua)
        ? "Chrome"
        : /Safari\//.test(ua)
          ? "Safari"
          : "Browser";
  const platform = /Android/.test(ua)
    ? "Android"
    : /iPhone|iPad/.test(ua)
      ? "iOS"
      : /Mac OS X/.test(ua)
        ? "macOS"
        : /Windows/.test(ua)
          ? "Windows"
          : /Linux/.test(ua)
            ? "Linux"
            : "";
  return platform ? `${engine} on ${platform}` : engine;
}
